import { ArrowDownRight, ArrowUpRight, Activity } from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import type { AnalyticsMetricDto } from '@/types/analytics'
import { formatMetricValue, formatDelta, type MetricFormat } from '@/utils/analyticsFormat'

interface AnalyticsStatTileProps {
    /** Two-line heading content (e.g. two stacked <span className="block"> elements). */
    title: ComponentChildren
    icon: ComponentChildren
    metric: AnalyticsMetricDto | null
    loading: boolean
    error: string | null
    /** How the value + delta render: count (default), percent (e.g. conversion rate), or duration. */
    format?: MetricFormat
}

/**
 * Shared presentational stat tile for a single analytics metric (ORB-328). Renders
 * the current value plus a growth pill derived from Delta / PreviousWindow, matching
 * the sibling dashboard tiles (e.g. TotalLeadsStatTile). The host fetches the
 * AnalyticsViewDto once and passes each tile its metric + shared loading/error state.
 */
export const AnalyticsStatTile = memo(({ title, icon, metric, loading, error, format = 'count' }: AnalyticsStatTileProps) => {
    if (loading) {
        return (
            <div className="tile">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-col gap-1">
                        <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
                        <div className="h-3 w-20 rounded-md bg-shimmer-light animate-shimmer" />
                    </div>
                    <span className="w-5 h-5 rounded-md bg-shimmer-light animate-shimmer" />
                </div>
                <div className="flex items-end gap-4 mt-auto">
                    <div className="flex-1">
                        <div className="h-8 w-20 mb-2 rounded-md bg-shimmer-light animate-shimmer" />
                        <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
                    </div>
                    <div className="h-5 w-12 rounded-md bg-shimmer-light animate-shimmer" />
                </div>
            </div>
        )
    }

    const delta = metric?.Delta ?? null
    let growthDir: 'up' | 'down' | 'flat' | null = null
    if (delta == null) {
        growthDir = null
    } else if (delta > 0) {
        growthDir = 'up'
    } else if (delta < 0) {
        growthDir = 'down'
    } else {
        growthDir = 'flat'
    }
    const prev = metric?.PreviousWindow ?? null
    // The window-over-window % change is only meaningful for counts; for percent/duration
    // metrics the absolute delta (points / time) is clearer, so suppress the parenthetical.
    const growthPct = format === 'count' && delta != null && prev != null && prev !== 0
        ? ((delta / prev) * 100).toFixed(1)
        : null

    return (
        <div className="tile">
            <div className="flex items-start justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
                    {title}
                </h3>
                {icon}
            </div>
            {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
            {!error && metric && (
                <div className="flex items-end justify-between gap-4">
                    <div className="text-4xl font-bold text-green-600 dark:text-green-400 leading-none">
                        {formatMetricValue(metric.Current, format)}
                    </div>
                    {growthDir && (
                        <div className="flex flex-col items-end gap-1">
                            <span
                                className={`pill ${growthDir === 'up' ? 'pill-up' : growthDir === 'down' ? 'pill-down' : 'pill-flat'} text-xs2`}
                                title="Difference vs previous window"
                            >
                                {growthDir === 'up' && <ArrowUpRight className="w-3.5 h-3.5" />}
                                {growthDir === 'down' && <ArrowDownRight className="w-3.5 h-3.5" />}
                                {growthDir === 'flat' && <Activity className="w-3.5 h-3.5" />}
                                <span>{delta === 0 ? formatDelta(0, format) : formatDelta(delta as number, format)}</span>
                                {growthPct !== null && growthDir !== 'flat' && <span className="opacity-70">({growthPct}%)</span>}
                            </span>
                        </div>
                    )}
                </div>
            )}
            {!error && !metric && <div className="text-sm text-slate-500 dark:text-slate-400">No data</div>}
        </div>
    )
})
